import React, { useState, useEffect, useRef } from 'react';
import './ShopByReels.css';
import { getImageUrl, getOptimizedUrl } from '../../../../constants/globalFunction';
import { FaPlay } from 'react-icons/fa';
import VideoModal from './VideoModal';

const ShopByReels = ({ videos, hasWhiteBg = false }) => {
  const [selectedVideoIndex, setSelectedVideoIndex] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isMobile, setIsMobile] = useState(false);
  const [windowWidth, setWindowWidth] = useState(window.innerWidth);
  const [currentVideoIndex, setCurrentVideoIndex] = useState(0);
  const reelsListRef = useRef(null);

  useEffect(() => {
    const checkIsMobile = () => {
      setIsMobile(window.innerWidth <= 768);
    };

    const handleResize = () => {
      setWindowWidth(window.innerWidth);
      checkIsMobile();
    };

    checkIsMobile();
    window.addEventListener('resize', handleResize);

    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Get card width based on screen size
  const getCardWidth = () => {
    if (windowWidth <= 480) return 160 + 8; // card width + gap
    if (windowWidth <= 768) return 200 + 12; // card width + gap
    if (windowWidth <= 1024) return 250 + 20; // card width + gap
    return 280 + 24; // card width + gap
  };

  // Update current index when user scrolls manually
  useEffect(() => {
    const scroller = reelsListRef.current;
    if (!scroller || !videos || videos.length === 0) return;

    let isScrolling = false;
    const handleScroll = () => {
      if (isScrolling) return;
      isScrolling = true;
      
      const cardWidth = getCardWidth();
      const scrollIndex = Math.round(scroller.scrollLeft / cardWidth);
      const currentIndex = Math.max(0, Math.min(scrollIndex, videos.length - 1));
      setCurrentVideoIndex(currentIndex);
      
      setTimeout(() => { isScrolling = false; }, 100);
    };

    scroller.addEventListener('scroll', handleScroll);
    return () => scroller.removeEventListener('scroll', handleScroll);
  }, [windowWidth, videos?.length]);

  // Auto-advance videos
  useEffect(() => {
    if (!reelsListRef.current || !videos || videos.length === 0) return;
    const scroller = reelsListRef.current;
    const intervalId = setInterval(() => {
      const cardWidth = getCardWidth();
      const maxVideos = isMobile ? 2 : 4;
      
      let nextIndex;
      if (currentVideoIndex + maxVideos >= videos.length) {
        nextIndex = 0;
      } else {
        nextIndex = currentVideoIndex + 1;
      }
      
      scroller.scrollTo({ left: nextIndex * cardWidth, behavior: "smooth" });
      setCurrentVideoIndex(nextIndex);
    }, 4000);
    return () => clearInterval(intervalId);
  }, [videos, windowWidth, isMobile, currentVideoIndex]);

  if (!videos || videos.length === 0) {
    return null;
  }

  const handleVideoClick = (index) => {
    setSelectedVideoIndex(index);
    setIsModalOpen(true);
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
    setSelectedVideoIndex(null);
  };

  // Determine how many videos to show based on screen size
  const maxVideos = isMobile ? 2 : 4;
  const shouldShowScroll = videos.length > maxVideos;

  // Show original grid layout for 4 or fewer videos (desktop) or 2 or fewer (mobile)
  if (!shouldShowScroll) {
  return (
    <>
      <section className={`home-shop-reels ${hasWhiteBg ? 'home-shop-reels--white' : ''}`}>
        <div className="home-shop-reels__header">
          <h2 className="home-shop-reels__title">SHOP BY REELS</h2>
        </div>
        
          <div className="home-shop-reels__grid-static">
          {videos.map((video, index) => (
            <div 
              key={video?._id || index} 
              className="home-shop-reels__item"
              onClick={() => handleVideoClick(index)}
            >
              <div className="home-shop-reels__thumbnail-container">
                {video?.image ? (
                  <img
                    src={getOptimizedUrl(video.image)}
                    alt={`Video ${index + 1}`}
                    className="home-shop-reels__thumbnail"
                    loading="lazy"
                  />
                ) : (
                  <div className="home-shop-reels__placeholder">
                    <span>No Thumbnail</span>
                  </div>
                )}
                <div className="home-shop-reels__play-overlay">
                  <div className="home-shop-reels__play-icon">
                    <FaPlay />
                  </div>
                </div>
              </div>
            </div>
          ))}
          </div>
        </section>

        {isModalOpen && selectedVideoIndex !== null && (
          <VideoModal
            videos={videos}
            currentIndex={selectedVideoIndex}
            isOpen={isModalOpen}
            onClose={handleCloseModal}
          />
        )}
      </>
    );
  }

  // Show horizontal scroll for more than max videos
  return (
    <>
      <section className={`home-shop-reels ${hasWhiteBg ? 'home-shop-reels--white' : ''}`}>
        <div className="home-shop-reels__header">
          <h2 className="home-shop-reels__title">SHOP BY REELS</h2>
        </div>
        
        <div className="home-shop-reels__container">
          <div
            className={`home-shop-reels__scroller ${isMobile ? 'home-shop-reels__scroller--mobile' : 'home-shop-reels__scroller--desktop'}`}
            ref={reelsListRef}
          >
            {videos.map((video, index) => (
              <div 
                key={video?._id || index} 
                className="home-shop-reels__scroll-item"
                onClick={() => handleVideoClick(index)}
              >
                <div className="home-shop-reels__thumbnail-container">
                  {video?.image ? (
                    <img
                      src={getOptimizedUrl(video.image)}
                      alt={`Video ${index + 1}`}
                      className="home-shop-reels__thumbnail"
                      loading="lazy"
                    />
                  ) : (
                    <div className="home-shop-reels__placeholder">
                      <span>No Thumbnail</span>
                    </div>
                  )}
                  <div className="home-shop-reels__play-overlay">
                    <div className="home-shop-reels__play-icon">
                      <FaPlay />
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {isModalOpen && selectedVideoIndex !== null && (
        <VideoModal
          videos={videos}
          currentIndex={selectedVideoIndex}
          isOpen={isModalOpen}
          onClose={handleCloseModal}
        />
      )}
    </>
  );
};

export default ShopByReels;

